import type { WorldPropTemplate } from '../../../../shared/scene/worldPropTemplates';
import { townColorHex as hex, type TownPropPalette } from './townPropTemplates';

/** All marks stay inside their catalog frame, with a solid bottom contact.
 * Flat facets and restrained highlights remain readable at gameplay scale.
 */
function outcrop(p: TownPropPalette): string {
  return `<g data-part="layered-outcrop">
    <path d="M4 88L10 64L26 57L37 32L60 28L75 8L105 17L116 46L132 56L140 88Z" fill="${hex(p.shade)}" />
    <path d="M10 64L26 57L37 32L60 28L75 8L84 39L70 58L38 67Z" fill="${hex(p.primary)}" />
    <path d="M75 8L105 17L116 46L84 39Z" fill="${hex(p.stone)}" />
    <path d="M26 57L61 51L70 58L111 51L132 56L118 64L64 69L39 76L10 77Z" fill="${hex(p.primary)}" />
    <path d="M37 32L61 34L75 8 M61 34L58 45 M38 67L70 58L84 39 M64 69L118 64"
      fill="none" stroke="${hex(p.roofEdge)}" stroke-width="1.7" opacity="0.55" />
    <path d="M14 82L33 74L54 78L63 88H6Z M111 76L125 71L141 88H110Z" fill="${hex(p.stone)}" />
    <path d="M39 32L54 28L62 30L58 35L44 37Z M18 67L31 61L44 62L40 66L24 70Z" fill="${hex(p.woodEdge)}" opacity="0.65" />
  </g>`;
}

function crystals(p: TownPropPalette): string {
  return `<g data-part="crystal-cluster">
    <path d="M4 124L12 113L36 109L65 112L88 111L97 124Z" fill="${hex(p.stone)}" />
    <path d="M28 114L24 30L42 3L57 29L59 115Z" fill="${hex(p.primary)}" />
    <path d="M42 3L41 31L45 116L59 115L57 29Z" fill="${hex(p.shade)}" />
    <path d="M25 30L42 3L41 31Z" fill="${hex(p.glassCore)}" opacity="0.75" />
    <path d="M9 64L23 49L36 70L41 118L19 117Z" fill="${hex(p.primary)}" />
    <path d="M23 49L26 72L31 116L41 118L36 70Z" fill="${hex(p.shade)}" />
    <path d="M52 115L61 49L79 31L88 53L77 117Z" fill="${hex(p.glass)}" />
    <path d="M79 31L76 57L64 118L77 117L88 53Z" fill="${hex(p.primary)}" />
    <path d="M71 116L83 85L94 76L97 96L87 120Z" fill="${hex(p.shade)}" />
    <path d="M30 35L34 93 M65 54L59 94 M15 68L19 86" fill="none" stroke="${hex(p.glassCore)}" stroke-width="2" opacity="0.8" />
    <path d="M20 122L31 117L40 121L50 118L64 124H20Z" fill="${hex(p.shade)}" />
  </g>`;
}

function frost(p: TownPropPalette): string {
  return `<g data-part="frost-shards">
    <path d="M3 116L16 106L39 105L66 99L101 104L121 116Z" fill="${hex(p.stone)}" />
    <path d="M17 109L18 63L39 38L44 85L53 112Z" fill="${hex(p.primary)}" />
    <path d="M39 38L31 74L35 111L53 112L44 85Z" fill="${hex(p.shade)}" />
    <path d="M43 111L53 36L79 2L79 76L88 112Z" fill="${hex(p.glass)}" />
    <path d="M79 2L63 51L62 112L88 112L79 76Z" fill="${hex(p.primary)}" />
    <path d="M72 111L90 70L115 50L106 94L108 111Z" fill="${hex(p.glass)}" />
    <path d="M115 50L96 88L91 111H108L106 94Z" fill="${hex(p.shade)}" />
    <path d="M55 41L76 9 M21 65L36 44 M89 73L110 56" stroke="${hex(p.glassCore)}" stroke-width="2" fill="none" />
    <path d="M38 111L52 103L66 106L71 100L94 108L105 106L117 116H9L22 109Z" fill="${hex(p.glassCore)}" />
    <path d="M51 74L61 69 M49 88L61 84 M84 95L93 91" stroke="${hex(p.glassCore)}" stroke-width="1.2" opacity="0.65" />
  </g>`;
}

function cairn(p: TownPropPalette): string {
  return `<g data-part="desert-cairn">
    <path d="M3 120L12 105L40 100L76 104L93 120Z" fill="${hex(p.stone)}" />
    <path d="M16 103L22 78L40 71L74 78L81 99L66 111L29 112Z" fill="${hex(p.shade)}" />
    <path d="M22 78L40 71L74 78L61 86L31 88Z" fill="${hex(p.primary)}" />
    <path d="M25 68L31 50L62 45L72 60L66 77L37 80Z" fill="${hex(p.primary)}" />
    <path d="M25 68L48 70L72 60L66 77L37 80Z" fill="${hex(p.shade)}" />
    <path d="M35 42L33 28L44 19L62 27L66 40L54 51L40 49Z" fill="${hex(p.primary)}" />
    <path d="M44 19L48 34L66 40L54 51L40 49L35 42L48 40Z" fill="${hex(p.shade)}" />
    <path d="M43 15L45 5L54 3L60 12L55 22L47 23Z" fill="${hex(p.primary)}" />
    <path d="M30 59L49 54L63 57 M26 93L45 90L69 93" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="2" opacity="0.6" />
    <path d="M41 57L38 66 M57 90L53 104" stroke="${hex(p.stone)}" stroke-width="1.5" />
    <path d="M8 116L21 110L31 115L43 112L59 118L78 112L91 120H5Z" fill="${hex(p.primary)}" />
  </g>`;
}

function mossLog(p: TownPropPalette): string {
  return `<g data-part="moss-log">
    <path d="M3 72L18 61L63 59L108 60L162 61L174 72Z" fill="${hex(p.stone)}" />
    <path d="M17 33L148 25Q168 29 168 49Q168 67 151 70L19 69Z" fill="${hex(p.wood)}" />
    <path d="M29 38L146 31 M28 51L154 43 M30 62L142 57" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="2" opacity="0.65" />
    <path d="M75 31L73 18L85 8L80 22L93 28 M126 28L140 16L137 28" fill="${hex(p.wood)}" />
    <ellipse cx="20" cy="51" rx="17" ry="21" fill="${hex(p.woodEdge)}" />
    <ellipse cx="20" cy="51" rx="12" ry="16" fill="${hex(p.wood)}" />
    <ellipse cx="20" cy="51" rx="7" ry="10" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="2" />
    <path d="M17 40Q27 44 24 54Q20 61 16 54L19 49" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.4" />
    <path d="M29 31Q44 21 55 29Q68 18 82 25Q101 19 112 25Q132 18 149 25L156 35L143 39L127 35L112 42L97 34L79 40L66 33L49 39L40 34Z" fill="${hex(p.primary)}" />
    <path d="M47 27L53 23L59 28 M85 24L92 22L100 25 M128 24L137 22L144 27" fill="none" stroke="${hex(p.glass)}" stroke-width="2.5" opacity="0.5" />
    <path d="M100 61L112 54L129 58L138 68L112 72H88Z" fill="${hex(p.shade)}" />
  </g>`;
}

function roots(p: TownPropPalette): string {
  return `<g data-part="root-bridge">
    <path d="M5 104Q30 86 44 48Q72 4 117 8Q164 9 181 50Q193 85 219 104H179Q167 78 150 53Q114 23 80 53Q63 75 49 104Z" fill="${hex(p.wood)}" />
    <path d="M17 101Q41 73 51 48Q79 11 115 17Q151 19 172 53Q184 83 204 101" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="5" />
    <path d="M45 102Q60 67 79 47Q111 23 145 46Q168 70 181 102" fill="none" stroke="${hex(p.shade)}" stroke-width="8" />
    <path d="M28 103Q61 82 67 46 M161 43Q166 80 196 103 M68 40Q113 26 157 46" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="2" />
    <path d="M45 48Q63 13 93 12L104 16L95 22L80 22L71 30L60 31L57 44Z M115 9Q154 9 169 31L155 27L146 22L129 21L121 18L111 18Z" fill="${hex(p.primary)}" />
    <path d="M9 104L27 94L34 100L49 94L60 104Z M171 104L181 96L194 99L203 94L220 104Z" fill="${hex(p.stone)}" />
    <path d="M86 16L89 7L93 16 M129 16L135 7L132 17 M61 34L59 23L66 30" fill="none" stroke="${hex(p.primary)}" stroke-width="2.5" />
  </g>`;
}

function arch(p: TownPropPalette): string {
  return `<g data-part="ruin-arch">
    <path d="M12 200V92L22 91V56L41 27L71 10L100 7L129 22L154 53V94L163 96V200H125V87L114 61L95 49H77L58 65L50 87V200Z" fill="${hex(p.primary)}" />
    <path d="M34 196V85L44 57L63 38L82 30L109 34L130 58L141 87V196H125V87L114 61L95 49H77L58 65L50 87V196Z" fill="${hex(p.shade)}" />
    <path d="M12 112H50 M12 146H50 M12 178H50 M125 116H163 M125 149H163 M125 177H163 M23 58L54 71 M42 29L67 54 M72 12L78 48 M101 9L96 49 M129 24L111 58 M153 56L124 73"
      fill="none" stroke="${hex(p.stone)}" stroke-width="3" />
    <path d="M16 109V95H31 M17 143V117H31 M128 145V120H141 M129 173V154H141" fill="none" stroke="${hex(p.roofEdge)}" stroke-width="1.4" opacity="0.6" />
    <path d="M74 13L83 7L97 7L101 30L86 38L75 30Z" fill="${hex(p.stone)}" />
    <path d="M82 20L87 15L93 21L87 28Z" fill="none" stroke="${hex(p.glass)}" stroke-width="2" opacity="0.7" />
    <path d="M28 148L33 160L27 167L34 180 M145 55L137 64L144 74L139 89" fill="none" stroke="${hex(p.shade)}" stroke-width="2" />
    <path d="M4 200L11 188L36 190L43 184L59 200Z M118 200L130 186L143 190L161 185L173 200Z" fill="${hex(p.stone)}" />
    <path d="M24 57L29 45L40 34L45 38L37 44L35 55L29 58Z M138 149L154 148L157 161L149 158L146 167L140 161Z" fill="${hex(p.woodEdge)}" opacity="0.6" />
  </g>`;
}

function standingStones(p: TownPropPalette): string {
  return `<g data-part="standing-stones">
    <path d="M5 152L10 89L23 67L43 71L49 145L74 152Z" fill="${hex(p.shade)}" />
    <path d="M10 89L23 67L30 87L28 146L5 152Z" fill="${hex(p.primary)}" />
    <path d="M45 145L50 20L67 4L91 12L101 148Z" fill="${hex(p.primary)}" />
    <path d="M67 4L75 25L73 147L101 148L91 12Z" fill="${hex(p.shade)}" />
    <path d="M50 20L67 4L91 12L75 25Z" fill="${hex(p.stone)}" />
    <path d="M101 145L98 70L115 48L133 63L138 152Z" fill="${hex(p.primary)}" />
    <path d="M115 48L119 79L116 150L138 152L133 63Z" fill="${hex(p.shade)}" />
    <path d="M61 48L56 56L62 63L68 55Z M62 64L60 87L66 95L58 104 M110 89L107 97L113 103" fill="none" stroke="${hex(p.glass)}" stroke-width="2" opacity="0.65" />
    <path d="M54 26L61 30L57 42 M77 105L84 113L82 125 M23 116L30 124L26 132" fill="none" stroke="${hex(p.stone)}" stroke-width="2" />
    <path d="M4 152L19 141L35 145L55 138L75 143L87 139L106 145L125 140L141 152Z" fill="${hex(p.stone)}" />
  </g>`;
}

function mushroom(p: TownPropPalette): string {
  return `<g data-part="giant-mushroom">
    <path d="M14 156L31 146L54 145L76 144L111 149L125 156Z" fill="${hex(p.stone)}" />
    <path d="M61 59Q67 90 54 127Q49 142 36 156H91Q75 137 80 115Q87 86 79 57Z" fill="${hex(p.woodEdge)}" />
    <path d="M74 63Q85 86 75 117Q69 139 79 153H63Q58 136 65 114Q75 84 66 66Z" fill="${hex(p.wood)}" opacity="0.5" />
    <path d="M11 61Q20 13 65 6Q99 5 124 53L126 62Q66 90 9 68Z" fill="${hex(p.primary)}" />
    <path d="M9 68Q60 51 126 62Q85 91 34 79Z" fill="${hex(p.shade)}" />
    <path d="M18 66L56 73 M34 65L61 72 M53 63L68 74 M75 63L74 74 M95 63L82 74 M115 64L91 74" stroke="${hex(p.glass)}" stroke-width="1.5" opacity="0.7" />
    <path d="M23 47Q39 20 67 16Q87 16 100 28" fill="none" stroke="${hex(p.glass)}" stroke-width="3" opacity="0.45" />
    <ellipse cx="42" cy="37" rx="7" ry="4" fill="${hex(p.glassCore)}" opacity="0.7" />
    <ellipse cx="82" cy="30" rx="8" ry="5" fill="${hex(p.glassCore)}" opacity="0.6" />
    <ellipse cx="103" cy="47" rx="5" ry="3" fill="${hex(p.glass)}" />
    <path d="M26 154L29 131 M106 154L103 133" stroke="${hex(p.woodEdge)}" stroke-width="5" />
    <path d="M15 133Q24 114 34 123L42 133Q29 141 15 133Z M92 135Q97 121 107 125L116 135Q104 141 92 135Z" fill="${hex(p.primary)}" />
    <path d="M17 133Q28 136 40 133 M94 135H113" fill="none" stroke="${hex(p.glass)}" stroke-width="2" />
  </g>`;
}

function reeds(p: TownPropPalette): string {
  const stalks = [[24, 31, 37, 21], [43, 10, 49, 8], [62, 26, 76, 13], [80, 18, 97, 5], [101, 45, 112, 34]];
  return `<g data-part="reed-bed">
    <path d="M10 112Q38 104 64 106Q100 103 123 112Z" fill="${hex(p.stone)}" />
    ${stalks.map(([x, y, tipX, tipY]) => `<path d="M${x} 110Q${x - 7} ${y + 30} ${tipX} ${tipY}"
      fill="none" stroke="${hex(p.wood)}" stroke-width="2.5" />
      <path d="M${tipX - 2} ${tipY + 18}L${tipX} ${tipY + 2}" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="5" stroke-linecap="round" />`).join('')}
    <path d="M29 112Q19 69 4 58Q27 68 39 112Z M39 112Q42 63 63 40Q51 77 52 112Z M67 112Q63 69 44 54Q70 61 77 112Z M82 112Q89 69 113 56Q94 79 96 112Z M97 112Q111 86 124 83L109 112Z" fill="${hex(p.primary)}" />
    <path d="M16 76Q27 93 29 109 M58 59Q49 90 49 109 M98 76Q88 98 88 110" fill="none" stroke="${hex(p.glass)}" stroke-width="1.2" opacity="0.5" />
    <path d="M14 112L24 101L27 109L43 102L46 110L62 102L64 111L77 104L80 111L97 103L103 112Z" fill="${hex(p.shade)}" />
  </g>`;
}

/** Uses the existing named prop path, palette grammar, cache, and ground anchoring. */
export function buildWorldPropBody(template: WorldPropTemplate, palette: TownPropPalette): string {
  switch (template) {
  case 'layered_outcrop': return outcrop(palette);
  case 'crystal_cluster': return crystals(palette);
  case 'frost_shards': return frost(palette);
  case 'desert_cairn': return cairn(palette);
  case 'moss_log': return mossLog(palette);
  case 'root_bridge': return roots(palette);
  case 'ruin_arch': return arch(palette);
  case 'standing_stones': return standingStones(palette);
  case 'giant_mushroom': return mushroom(palette);
  case 'reed_bed': return reeds(palette);
  }
}
